<template>
  <van-popup
    v-model="popupVisible"
    class="room-picker-popup"
    position="bottom"
    round
    :close-on-click-overlay="false"
  >
    <section class="room-picker" aria-label="选择房间">
      <div class="room-search">
        <input
          v-model.trim="keyword"
          type="search"
          placeholder="请输入房间号查询"
          aria-label="请输入房间号查询"
        />
        <span class="search-icon" aria-hidden="true"></span>
      </div>

      <nav class="category-tabs" aria-label="房间分类">
        <button
          v-for="category in categoryOptions"
          :key="category"
          type="button"
          class="category-tab"
          :class="{ active: activeCategory === category }"
          @click="changeCategory(category)"
        >
          {{ category }}
        </button>
      </nav>

      <div class="status-summary">
        <button
          v-for="status in statusSummary"
          :key="status.key"
          type="button"
          class="summary-item"
          :class="{ active: activeStatus === status.key }"
          @click="activeStatus = status.key"
        >
          <span>{{ status.label }}</span>
          <strong>{{ status.count }}</strong>
        </button>
      </div>

      <div class="room-list">
        <ul v-if="visibleRooms.length" class="room-grid">
          <li
            v-for="room in visibleRooms"
            :key="room.id"
            class="room-card"
            :class="[
              `room-${room.status}`,
              { selected: internalSelectedId === room.id },
            ]"
            @click="toggleSelection(room.id)"
          >
            <div class="card-main">
              <div class="card-title">
                <strong>{{ room.name }}</strong>
                <span>{{ room.type }}</span>
              </div>
              <template v-if="room.status === 'reserved'">
                <p class="gold-line">{{ room.handCode }} {{ room.guestName }}</p>
                <p class="reserve-time">{{ room.reserveTime }}</p>
              </template>
              <template v-else>
                <p
                  v-for="(line, index) in room.detailLines || []"
                  :key="index"
                  :class="{ 'gold-line': index === 0 }"
                >
                  {{ line }}
                </p>
              </template>
              <p v-if="room.elapsed" class="elapsed">
                {{ room.elapsed }}<span v-if="room.timeout">将超时</span>
              </p>
            </div>
            <div class="status-bar">{{ getStatusLabel(room.status) }}</div>
          </li>
        </ul>
        <div v-else class="empty-state">暂无符合条件的房间</div>
      </div>

      <footer class="picker-footer">
        <button type="button" class="cancel-button" @click="handleCancel">取消</button>
        <button
          type="button"
          class="confirm-button"
          :disabled="!selectedRoom"
          @click="handleConfirm"
        >
          确认
        </button>
      </footer>
    </section>
  </van-popup>
</template>

<script>
const ALL_CATEGORY = "全部";
const ALL_STATUS = "all";

export default {
  name: "RoomSelectPopup",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    rooms: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: [String, Number],
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      keyword: "",
      activeCategory: ALL_CATEGORY,
      activeStatus: ALL_STATUS,
      internalSelectedId: null,
      statusOptions: [
        { key: ALL_STATUS, label: "全部" },
        { key: "idle", label: "空闲" },
        { key: "occupied", label: "占用" },
        { key: "cleaning", label: "清扫" },
      ],
    };
  },
  computed: {
    /**
     * 将组件 v-model 映射到 van-popup，并通过 input 事件同步父页面显示状态。
     */
    popupVisible: {
      get() {
        return this.value;
      },
      set(visible) {
        this.$emit("input", visible);
      },
    },
    /**
     * 优先使用父页面传入的分类；未传时根据房间 category 字段自动去重生成。
     */
    categoryOptions() {
      const source = this.categories.length
        ? this.categories
        : this.rooms.map((room) => room.category).filter(Boolean);
      return [ALL_CATEGORY, ...new Set(source.filter((item) => item !== ALL_CATEGORY))];
    },
    /**
     * 应用房型和房间号搜索条件，供状态统计和最终列表共同使用。
     */
    categoryFilteredRooms() {
      const normalizedKeyword = this.keyword.toLowerCase();
      return this.rooms.filter((room) => {
        const matchesCategory =
          this.activeCategory === ALL_CATEGORY ||
          room.category === this.activeCategory;
        const matchesKeyword =
          !normalizedKeyword ||
          String(room.name).toLowerCase().includes(normalizedKeyword);
        return matchesCategory && matchesKeyword;
      });
    },
    /**
     * 根据已应用的房型和搜索条件实时计算房态数量。
     */
    statusSummary() {
      return this.statusOptions.map((option) => ({
        ...option,
        count:
          option.key === ALL_STATUS
            ? this.categoryFilteredRooms.length
            : this.categoryFilteredRooms.filter(
                (room) => room.status === option.key
              ).length,
      }));
    },
    /**
     * 应用最后一层房态筛选，生成实际展示的房间卡片。
     */
    visibleRooms() {
      return this.activeStatus === ALL_STATUS
        ? this.categoryFilteredRooms
        : this.categoryFilteredRooms.filter(
            (room) => room.status === this.activeStatus
          );
    },
    /**
     * 根据临时选中 id 返回完整房间对象，确认前不会修改父页面表单。
     */
    selectedRoom() {
      return (
        this.rooms.find((room) => room.id === this.internalSelectedId) || null
      );
    },
  },
  watch: {
    /**
     * 打开弹窗时重置搜索和筛选，并回显父页面已经确认的房间。
     */
    value(visible) {
      if (visible) {
        this.keyword = "";
        this.activeCategory = ALL_CATEGORY;
        this.activeStatus = ALL_STATUS;
        this.internalSelectedId = this.selectedId;
      }
    },
  },
  methods: {
    /**
     * 切换房型并恢复全部房态，避免新房型因旧房态条件而显示空列表。
     */
    changeCategory(category) {
      this.activeCategory = category;
      this.activeStatus = ALL_STATUS;
    },
    /**
     * 切换房间单选状态；点击新房间替换旧选择，再点当前房间则取消选择。
     */
    toggleSelection(roomId) {
      this.internalSelectedId =
        this.internalSelectedId === roomId ? null : roomId;
    },
    /**
     * 返回完整房间对象并关闭弹窗，未选择房间时不执行。
     */
    handleConfirm() {
      if (!this.selectedRoom) {
        return;
      }
      this.$emit("confirm", { ...this.selectedRoom });
      this.popupVisible = false;
    },
    /**
     * 清除临时选择、通知父页面取消并关闭，不修改父页面已确认的房间。
     */
    handleCancel() {
      this.internalSelectedId = null;
      this.$emit("cancel");
      this.popupVisible = false;
    },
    /**
     * 将房态键转换为卡片底部中文文案，未知状态使用“未知”兜底。
     */
    getStatusLabel(status) {
      const map = {
        preClock: "预上钟",
        occupied: "有人",
        idle: "空闲",
        working: "按摩中",
        reserved: "预定",
        cleaning: "清扫",
        maintenance: "维修",
        retained: "留房",
      };
      return map[status] || "未知";
    },
  },
};
</script>

<style scoped lang="scss">
.room-picker-popup {
  height: 88vh;
  overflow: hidden;
  background: #f7f1e7;
}

.room-picker {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  background: #f7f1e7;
}

.room-search {
  position: relative;
  padding: 16px 18px 14px;
  background: #f7f4ef;

  input {
    width: 100%;
    height: 54px;
    padding: 0 72px 0 34px;
    border: 0;
    border-radius: 27px;
    box-sizing: border-box;
    font-size: 24px;
    outline: 0;
  }
}

.search-icon {
  position: absolute;
  right: 48px;
  top: 31px;
  width: 18px;
  height: 18px;
  border: 3px solid #aa9766;
  border-radius: 50%;
}

.category-tabs {
  display: flex;
  height: 80px;
  flex: 0 0 80px;
  overflow-x: auto;
  background: #eee6da;
}

.category-tab {
  position: relative;
  min-width: 152px;
  border: 0;
  background: transparent;
  font-size: 26px;

  &.active::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 56px;
    height: 5px;
    background: #252932;
    transform: translateX(-50%);
  }
}

.status-summary {
  display: flex;
  height: 52px;
  margin: 22px 14px 18px;
  flex: 0 0 52px;
  overflow-x: auto;
  border-radius: 27px;
  background: #fff;
}

.summary-item {
  display: flex;
  min-width: 145px;
  flex: 1 0 145px;
  align-items: center;
  justify-content: center;
  gap: 18px;
  border: 0;
  color: #aa9361;
  background: transparent;
  font-size: 24px;

  &.active {
    border-radius: 27px;
    background: #f3eadc;
    box-shadow: inset 0 0 0 1px #ddcbae;
  }
}

.room-list {
  min-height: 0;
  padding: 0 13px 18px;
  flex: 1;
  overflow-y: auto;
}

.room-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.room-card {
  display: flex;
  height: 224px;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border: 2px solid #d8d9df;
  border-radius: 14px;
  box-sizing: border-box;
  background: #fff;

  &.selected {
    border-color: #f24b55;
    box-shadow: 0 0 0 1px rgba(242, 75, 85, 0.12);
  }
}

.card-main {
  display: flex;
  min-height: 0;
  padding: 13px 13px 8px;
  flex: 1;
  overflow: hidden;
  flex-direction: column;

  p {
    margin: 7px 0 0;
    overflow: hidden;
    color: #6f6b65;
    font-size: 17px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .gold-line {
    color: #ad9157;
    font-weight: 600;
  }
}

.card-title {
  display: flex;
  justify-content: space-between;

  strong {
    font-size: 29px;
  }

  span {
    overflow: hidden;
    font-size: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.card-main .elapsed {
  margin-top: auto;
  color: #f34352;
  font-size: 28px;
  font-weight: 700;

  span {
    margin-left: 7px;
    font-size: 18px;
  }
}

.card-main .reserve-time {
  margin-top: 18px;
  color: #18c4df;
  font-size: 22px;
  font-weight: 600;
}

.status-bar {
  display: flex;
  height: 45px;
  flex: 0 0 45px;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(90deg, #8788ae, #b1b1cc);
  font-size: 25px;
}

.room-preClock .status-bar {
  background: linear-gradient(90deg, #ed2630, #ff5961);
}

.room-occupied .status-bar {
  background: linear-gradient(90deg, #7776ea, #8e86f5);
}

.room-working .status-bar {
  background: linear-gradient(90deg, #657bea, #52b4e8);
}

.room-reserved .status-bar {
  background: linear-gradient(90deg, #18bae4, #04dce4);
}

.room-cleaning .status-bar,
.room-maintenance .status-bar {
  background: linear-gradient(90deg, #999fa3, #d3d7dc);
}

.room-retained .status-bar {
  background: linear-gradient(90deg, #ff812d, #ffc10d);
}

.empty-state {
  padding: 100px 0;
  text-align: center;
}

.picker-footer {
  display: flex;
  height: calc(102px + env(safe-area-inset-bottom));
  padding: 12px 24px env(safe-area-inset-bottom);
  flex: 0 0 auto;
  justify-content: space-between;
  border-top: 1px solid #eadfce;
  box-sizing: border-box;
  background: #fffdfa;

  button {
    width: 335px;
    height: 72px;
    border-radius: 36px;
    font-size: 28px;
  }
}

.cancel-button {
  border: 1px solid #ded7ca;
  color: #a98e59;
  background: #faf7f1;
}

.confirm-button {
  border: 0;
  color: #fff;
  background: linear-gradient(90deg, #d0ad68, #f0c875);

  &:disabled {
    color: #c7bda9;
    background: #e8e2d8;
  }
}
</style>
